// Canonical team roster + identity resolver for the Agents app (AMB-358 / A1, module 1).
//
// "Who is on the team" and "which member is the logged-in user". Originally a single
// hardcoded list of Ambid's members; now PER-TENANT (AMB-438): on load it fetches the
// signed-in agency's own members from the operator (GET /api/agents/members) and uses those
// instead. The hardcoded ROSTER below remains the FALLBACK — it's what's used when the
// operator returns nothing (the Ambid dogfood has no operator-side roster), so Ambid keeps
// working unchanged while every other tenant sees its own people.
//
// The hard rule (D-1): an unrecognized login resolves to null — NEVER a default person. A
// null lets the UI fall back to a neutral "Everyone" view instead of showing one user as
// another.

import { useEffect, useSyncExternalStore } from "react";
import { agentsJson } from "./api";

export interface RosterMember {
  id: string; // slug used everywhere as the owner/reviewer/submitter key ("kyle")
  name: string;
  email: string | null; // the address this member logs in with
  initials: string;
  color: string; // Tailwind bg-* class for the avatar
}

// The Ambid team — the FALLBACK roster, used when the operator returns no members (the
// dogfood tenant, which has no operator-side roster.json). Real tenants override this with
// their own members at load time.
export const ROSTER: RosterMember[] = [
  { id: "kyle", name: "Kyle Graham", email: "kjgraham@gmail.com", initials: "KG", color: "bg-blue-500" },
  { id: "claire", name: "Claire Fabella", email: "clairefabella+04@ambidtech.com", initials: "CF", color: "bg-green-500" },
  { id: "tim", name: "Tim", email: "fatima@ambidtech.com", initials: "TM", color: "bg-amber-500" },
  { id: "ryan", name: "Ryan Morta", email: "ryanmorta@ambidtech.com", initials: "RM", color: "bg-orange-500" },
  { id: "hannah", name: "Hannah Abalos", email: "hannahabalos@ambidtech.com", initials: "HA", color: "bg-violet-500" },
  { id: "jennifer", name: "Jennifer Sapida", email: "jennifersapida@ambidtech.com", initials: "JS", color: "bg-pink-500" },
];

const PALETTE = ["bg-blue-500", "bg-green-500", "bg-amber-500", "bg-orange-500", "bg-violet-500", "bg-pink-500", "bg-teal-500", "bg-rose-500"];

const norm = (s: string | null | undefined): string => (s || "").trim().toLowerCase();

function initialsFrom(name: string): string {
  return (
    name
      .split(/[\s/]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?"
  );
}

// Map an operator member record { id, name, email, role } → a display RosterMember,
// deriving the initials + a stable avatar color the operator doesn't store.
function toRosterMember(m: any, i: number): RosterMember {
  const name = (m && (m.name || m.email || m.id)) || "Member";
  return {
    id: String(m.id),
    name,
    email: m.email ?? null,
    initials: m.initials || initialsFrom(name),
    color: m.color || PALETTE[i % PALETTE.length],
  };
}

// ── Reactive per-tenant roster store (mirrors activeClient.ts) ────────────────────────────
let activeRoster: RosterMember[] = ROSTER; // fallback until the tenant's members load
const listeners = new Set<() => void>();

export function getActiveRoster(): RosterMember[] {
  return activeRoster;
}

// Exported so tests can stage a tenant roster, and so non-React callers can set it directly.
export function setActiveRoster(members: RosterMember[]): void {
  activeRoster = members;
  listeners.forEach((l) => l());
}

function subscribeRoster(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Reactive hook — re-renders the caller when the tenant roster loads/changes. */
export function useRoster(): RosterMember[] {
  return useSyncExternalStore(subscribeRoster, getActiveRoster, getActiveRoster);
}

// Load the signed-in tenant's members once. A non-empty operator roster replaces the
// fallback; an empty one (Ambid dogfood) leaves ROSTER in place. Idempotent.
let loaded = false;
let inFlight: Promise<void> | null = null;
export function loadTenantRoster(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (inFlight) return inFlight;
  inFlight = agentsJson<{ members?: any[] }>("/api/agents/members")
    .then((res) => {
      const members = Array.isArray(res?.members) ? res.members : [];
      if (members.length) setActiveRoster(members.map(toRosterMember));
      loaded = true;
    })
    .catch(() => {
      /* operator unreachable / not entitled → keep the fallback roster */
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Trigger the tenant-roster load once from any component that needs identity/people. */
export function useEnsureRoster(): void {
  useEffect(() => {
    void loadTenantRoster();
  }, []);
}

// ── Identity + lookups (default to the ACTIVE roster, so they follow the tenant) ──────────

// Admins may delete any review (B4). The dogfood owner is "kyle"; a real tenant's owner
// carries role "owner"/"admin" in the loaded roster.
const ADMIN_IDS = new Set(["kyle"]);

/** Whether a member can perform admin-only actions (e.g. delete any review). */
export function isAdmin(memberId: string | null | undefined, roster: RosterMember[] = getActiveRoster()): boolean {
  if (!memberId) return false;
  if (ADMIN_IDS.has(memberId)) return true;
  const m = roster.find((x) => x.id === memberId) as any;
  return !!m && (m.role === "owner" || m.role === "admin");
}

/**
 * Resolve a login email to a member id, or null when there's no match.
 * Pure + case/whitespace insensitive. Never returns a default person — unknown email → null.
 */
export function resolveMember(
  email: string | null | undefined,
  roster: RosterMember[] = getActiveRoster(),
): string | null {
  const e = norm(email);
  if (!e) return null;
  const hit = roster.find((m) => norm(m.email) === e);
  return hit ? hit.id : null;
}

/** Look up a member record by id (for name/avatar), or undefined if not on the roster. */
export function memberById(id: string | null | undefined, roster: RosterMember[] = getActiveRoster()): RosterMember | undefined {
  if (!id) return undefined;
  return roster.find((m) => m.id === id);
}

/**
 * The current signed-in member's id, from the authenticated user in localStorage ("user").
 * Returns null for an unrecognized or absent user — the caller renders the neutral "Everyone"
 * view (and may flag the account as unlinked).
 */
export function getCurrentMemberId(roster: RosterMember[] = getActiveRoster()): string | null {
  try {
    const email = JSON.parse(localStorage.getItem("user") || "{}").email;
    return resolveMember(email, roster);
  } catch {
    return null;
  }
}
